<script>
import { formButton, formInput, formPhone } from 'tb-design_system-web'
import { useForm } from 'vee-validate'
import { ref, provide, onMounted } from 'vue'
import { useMeta } from 'vue-meta'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import * as yup from 'yup'

import RegisterLayout from '@/components/shared/registerLayout.vue'
import StepProgressBar from '@/components/shared/stepProgressBar.vue'
import { validateStepAccess } from '@/composables/utils/onboardingGuards'
import { isValidIntlPhone } from '@/composables/utils/phoneGoogle'
import { validateExistingEmail } from '@/services/api'
import { handleGlobalError } from '@/services/errorHandler'
import { useOnboardingStore } from '@/stores/onboarding'

export default {
  name: 'RegisterPJStep2',
  components: {
    RegisterLayout,
    StepProgressBar,
    formInput,
    formButton,
    formPhone
  },

  setup() {
    useMeta({ title: 'Cadastro PJ - Etapa 2' })

    const router = useRouter()
    const onboardingStore = useOnboardingStore()

    onMounted(() => {
      validateStepAccess(onboardingStore, 'PJ', [1], router)
    })

    const step2Data = onboardingStore.getStepData(2) || {}

    const nameValue = ref(step2Data.name || '')
    const emailValue = ref(step2Data.email || '')
    const phoneValue = ref(step2Data.phone || '')

    const loading = ref(false)

    const emailExists = ref(false)
    const emailExistsMessage = ref('')

    const alreadySubmittedOnce = ref(false)
    provide('alreadySubmittedOnce', alreadySubmittedOnce)

    const { handleSubmit, setFieldError } = useForm({
      validationSchema: yup.object({
        name: yup
          .string()
          .required('Nome é obrigatório')
          .min(6, 'Mínimo de 6 caracteres')
          .max(100, 'Máximo de 100 caracteres')
          .test(
            'full-name',
            'Informe pelo menos nome e sobrenome, com 2 letras ou mais cada',
            (value) => {
              if (!value) return false
              const words = value.trim().split(/\s+/)
              return words.length >= 2 && words.filter((word) => word.length >= 2).length >= 2
            }
          ),
        email: yup.string().required('E-mail é obrigatório').email('E-mail inválido'),
        phone: yup
          .string()
          .required('Telefone é obrigatório')
          .test('is-valid-phone', 'Telefone inválido', (value) => isValidIntlPhone(value))
      })
    })

    const onSubmitAction = () => {
      alreadySubmittedOnce.value = true
      return onSubmit()
    }

    const onSubmit = handleSubmit(async () => {
      loading.value = true
      emailExists.value = false
      emailExistsMessage.value = ''

      try {
        const emailDuplicated = await validateEmailExists()
        if (emailDuplicated) {
          loading.value = false
          return
        }

        onboardingStore.setStepData(2, {
          name: nameValue.value,
          email: emailValue.value,
          phone: phoneValue.value
        })

        router.push('/register/pj/step-3')
      } catch (err) {
        handleGlobalError(err)
      } finally {
        loading.value = false
      }
    })

    const validateEmailExists = async () => {
      try {
        const emailRes = await validateExistingEmail(emailValue.value.trim())
        if (emailRes?.data?.payload?.exists === true) {
          setFieldError('email', 'E-mail já cadastrado')
          emailExists.value = true
          emailExistsMessage.value = 'E-mail já cadastrado'
          toast('E-mail já cadastrado.', { type: 'error', autoClose: 3000 })
          return true
        }
      } catch (err) {
        handleGlobalError(err, 'Erro ao verificar e-mail:')
      }

      return false
    }

    return {
      nameValue,
      emailValue,
      phoneValue,
      loading,
      onSubmitAction
    }
  }
}
</script>

<template>
  <RegisterLayout>
    <div class="col-12 col-md-9 h-100 onboarding pb-5">
      <div class="row">
        <StepProgressBar
          class="mt-5 mb-5"
          :steps="['Empresa', 'Responsável', 'Demanda', 'Senha']"
          :current="2"
        />
      </div>
      <div class="row d-flex justify-content-center mt-4">
        <div class="col-12 col-md-6 col-lg-5 d-flex flex-column align-items-center">
          <form-input
            class="col-12 form-input mb-3"
            label="Nome completo"
            v-model="nameValue"
            id="name"
            name="name"
            type="name"
          />
          <form-input
            class="col-12 form-input mb-3"
            label="E-mail"
            v-model="emailValue"
            id="email"
            name="email"
            type="email"
          />
          <form-phone
            class="col-12 form-input"
            name="phone"
            label="DDD + Telefone"
            v-model="phoneValue"
          />
          <form-button
            class="col-12 form-input"
            title="Próximo"
            :loading="loading"
            :isDisabled="loading"
            @clicked="onSubmitAction"
          />
        </div>
      </div>
    </div>
  </RegisterLayout>
</template>

<style lang="scss" scoped></style>
